import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Eye, MapPin, Users, MessageSquare, ChevronRight, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/** Read-only admin chat browser: lists every chat group and lets admin drill in to read
 * all messages (including system messages). No moderation actions here — the violations
 * panel handles enforcement. */
export default function AdminChatMonitorPanel({ API, token, lang }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // group being viewed
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const isID = lang !== 'en';
  const initialMountRef = useRef(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadGroups = useCallback(async ({ silent = false } = {}) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/chat/groups`, { headers });
      setGroups(r.data.groups || []);
    } catch (e) {
      // Skip the toast on the initial mount to avoid a "Failed to load" flash
      // when the production backend is warming up.
      if (!silent && !initialMountRef.current) {
        toast.error(e.response?.data?.detail || 'Failed to load chat groups');
      }
    } finally { setLoading(false); }
  }, [API, headers]);

  useEffect(() => {
    loadGroups({ silent: true }).finally(() => { initialMountRef.current = false; });
  }, [loadGroups]);

  const openGroup = async (g) => {
    setActive(g);
    setMsgLoading(true);
    setMessages([]);
    try {
      const r = await axios.get(`${API}/admin/chat/messages`, {
        params: { group_id: g.id, limit: 300 },
        headers,
      });
      setMessages(r.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load messages');
    } finally { setMsgLoading(false); }
  };

  const filteredGroups = groups.filter(g => {
    if (scopeFilter !== 'all' && g.scope !== scopeFilter) return false;
    if (typeFilter !== 'all' && (g.group_type || 'city') !== typeFilter) return false;
    return true;
  });

  return (
    <div className="glass-card rounded-xl p-5" data-testid="admin-chat-monitor">
      <div className="flex items-center gap-2 mb-4">
        <Eye className="w-5 h-5 text-[#D98A94]" />
        <h3 className="t-text font-semibold">
          {isID ? 'Monitor Chat (Baca Semua Grup)' : 'Chat Monitor (Read All Groups)'}
        </h3>
      </div>

      {active ? (
        <ChatDetailView
          group={active}
          messages={messages}
          loading={msgLoading}
          lang={lang}
          onBack={() => { setActive(null); setMessages([]); }}
        />
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <select value={scopeFilter} onChange={e => setScopeFilter(e.target.value)} data-testid="chat-monitor-scope"
              className="rounded-full px-3 py-1.5 t-bg-input t-text border t-border">
              <option value="all">{isID ? 'Semua Kategori' : 'All Scopes'}</option>
              <option value="buddhist">Buddhist</option>
              <option value="katolik">Katolik</option>
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="chat-monitor-type"
              className="rounded-full px-3 py-1.5 t-bg-input t-text border t-border">
              <option value="all">{isID ? 'Semua Tipe' : 'All Types'}</option>
              <option value="city">{isID ? 'Kota' : 'City'}</option>
              <option value="province">{isID ? 'Provinsi' : 'Province'}</option>
              <option value="event">{isID ? 'Acara/Event' : 'Event'}</option>
            </select>
            <span className="ml-auto text-[11px] t-text-muted self-center">
              {filteredGroups.length} {isID ? 'grup' : 'groups'} · {groups.reduce((s, g) => s + (g.message_count || 0), 0)} {isID ? 'pesan total' : 'total messages'}
            </span>
          </div>

          {loading ? (
            <div className="py-10 text-center t-text-muted text-sm">{isID ? 'Memuat...' : 'Loading...'}</div>
          ) : filteredGroups.length === 0 ? (
            <div className="py-10 text-center t-text-muted text-sm">
              {isID ? 'Belum ada grup chat.' : 'No chat groups yet.'}
            </div>
          ) : (
            <div className="max-h-[65vh] overflow-y-auto space-y-1.5">
              {filteredGroups.map(g => (
                <button
                  key={g.id}
                  onClick={() => openGroup(g)}
                  data-testid={`chat-monitor-group-${g.id}`}
                  className="w-full text-left rounded-lg p-3 t-bg-input hover:border-[#D98A94]/40 border border-transparent transition flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(217,138,148,0.15)' }}>
                    {g.group_type === 'province'
                      ? <MapPin className="w-4 h-4 text-[#D98A94]" />
                      : g.group_type === 'event'
                        ? <Sparkles className="w-4 h-4 text-[#D98A94]" />
                        : <Users className="w-4 h-4 text-[#D98A94]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold t-text truncate">{g.name}</div>
                    <div className="text-[11px] t-text-muted truncate">
                      <span className="uppercase tracking-wide">{g.scope}</span>
                      {' · '}{g.group_type || 'city'}
                      {' · '}{g.member_count} {isID ? 'anggota' : 'members'}
                      {' · '}{g.message_count} {isID ? 'pesan' : 'msgs'}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 t-text-muted shrink-0" />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChatDetailView({ group, messages, loading, lang, onBack }) {
  const isID = lang !== 'en';
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={onBack} data-testid="chat-monitor-back"
          className="rounded-full p-1.5 t-bg-input hover:text-[#D98A94]" aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold t-text truncate">{group.name}</div>
          <div className="text-[11px] t-text-muted">
            {isID ? 'ID grup:' : 'Group ID:'} <span className="font-mono">{group.id}</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl p-2 max-h-[65vh] overflow-y-auto space-y-2" style={{ background: 'var(--bg-input)' }}>
        {loading ? (
          <div className="py-10 text-center t-text-muted text-sm">{isID ? 'Memuat pesan...' : 'Loading messages...'}</div>
        ) : messages.length === 0 ? (
          <div className="py-10 text-center t-text-muted text-sm">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {isID ? 'Belum ada pesan di grup ini.' : 'No messages in this group yet.'}
          </div>
        ) : messages.map(m => (
          <MessageRow key={m.id} m={m} isID={isID} />
        ))}
      </div>
      <div className="text-[10px] t-text-muted text-right">
        {isID
          ? `Menampilkan ${messages.length} pesan terbaru (mode baca-only).`
          : `Showing ${messages.length} recent messages (read-only).`}
      </div>
    </div>
  );
}

function MessageRow({ m, isID }) {
  const created = m.created_at ? new Date(m.created_at) : null;
  const timeStr = created && !isNaN(created.getTime())
    ? created.toLocaleString(isID ? 'id-ID' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })
    : '';
  if (m.is_system) {
    return (
      <div className="rounded-lg p-2.5 text-xs" style={{ background: 'rgba(217,138,148,0.08)', border: '1px solid rgba(217,138,148,0.20)' }} data-testid={`chat-monitor-msg-${m.id}`}>
        <div className="flex items-center gap-1 mb-1">
          <Sparkles className="w-3 h-3 text-[#D98A94]" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#D98A94]">
            {m.system_type || 'system'}
          </span>
          <span className="ml-auto text-[10px] t-text-muted">{timeStr}</span>
        </div>
        <div className="t-text whitespace-pre-line">{m.text}</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg p-2.5 bg-white/50 dark:bg-white/5" data-testid={`chat-monitor-msg-${m.id}`}>
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-xs font-semibold t-text truncate">{m.author_name || '(?)'}</span>
        <span className="text-[10px] t-text-muted ml-auto shrink-0">{timeStr}</span>
      </div>
      {m.text && <div className="text-sm t-text whitespace-pre-wrap break-words">{m.text}</div>}
      {m.media_url && (
        <div className="text-[11px] mt-1 t-text-muted italic">
          [📎 {m.media_type || 'media'}: <span className="font-mono">{m.media_url}</span>]
        </div>
      )}
      {m.poll_id && (
        <div className="text-[11px] mt-1 t-text-muted italic">[📊 {isID ? 'Polling' : 'Poll'}]</div>
      )}
    </div>
  );
}
